import posthog from 'posthog-js'

const JS_ATTEMPT_ID_KEY = 'js_attempt_id'
const JS_USER_ID_KEY = 'js_user_id'
const JS_USER_EMAIL_KEY = 'js_user_email'
const JOBSEEKER_INSTANCE_NAME = 'jobseeker'
const FLOW_VERSION = 'v1'

const enabled = import.meta.env.VITE_POSTHOG_ENABLED === 'true'

export const jobseekerPosthogClient = enabled
    ? posthog.init(
          import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
          {
              api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
              capture_pageview: true,
              capture_pageleave: true,
              capture_exceptions: true,
              session_recording: {
                  maskAllInputs: false,
              },
              debug: import.meta.env.VITE_POSTHOG_DEBUG_MODE === 'true',
          },
          JOBSEEKER_INSTANCE_NAME,
      )
    : null

const getSessionStorage = (): Storage | null => {
    if (typeof window === 'undefined') return null
    try {
        return window.sessionStorage
    } catch {
        return null
    }
}

const getFromSession = (key: string): string | null => {
    const storage = getSessionStorage()
    if (!storage) return null
    try {
        return storage.getItem(key)
    } catch {
        return null
    }
}

const setInSession = (key: string, value: string): void => {
    const storage = getSessionStorage()
    if (!storage) return
    try {
        storage.setItem(key, value)
    } catch {
        // ignore write failures
    }
}

const generateUuid = (): string => {
    if (
        typeof crypto !== 'undefined' &&
        typeof crypto.randomUUID === 'function'
    ) {
        return crypto.randomUUID()
    }

    const segment = () =>
        Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1)

    return `${segment()}${segment()}-${segment()}-${segment()}-${segment()}-${segment()}${segment()}${segment()}`
}

export const getJsAttemptId = (): string | null => {
    return getFromSession(JS_ATTEMPT_ID_KEY)
}

export const ensureJsAttemptId = (): string => {
    const existing = getJsAttemptId()
    if (existing) return existing

    const next = generateUuid()
    setInSession(JS_ATTEMPT_ID_KEY, next)
    return next
}

export const getJsUserId = (): string | null => {
    return getFromSession(JS_USER_ID_KEY)
}

export const setJsUserId = (userId: string): void => {
    if (!userId) return
    setInSession(JS_USER_ID_KEY, userId)
}

export const getJsUserEmail = (): string | null => {
    return getFromSession(JS_USER_EMAIL_KEY)
}

export const setJsUserEmail = (email: string): void => {
    if (!email) return
    setInSession(JS_USER_EMAIL_KEY, email)
}

const getIsMobile = (): boolean => {
    if (typeof window === 'undefined') return false
    if (typeof window.matchMedia !== 'function') return false
    return window.matchMedia('(max-width: 768px)').matches
}

const getPagePath = (): string | undefined => {
    if (typeof window === 'undefined') return undefined
    return window.location?.pathname
}

export const jsCapture = (event: string, props?: Record<string, any>): void => {
    if (!jobseekerPosthogClient) return

    const attemptId = ensureJsAttemptId()
    jobseekerPosthogClient.capture(event, {
        attempt_id: attemptId,
        flow_version: FLOW_VERSION,
        page_path: getPagePath(),
        is_mobile: getIsMobile(),
        area: 'jobseeker',
        ...props,
    })
}

export const jsIdentify = (
    userId: string,
    extraProps?: Record<string, any>,
): void => {
    if (!jobseekerPosthogClient) return
    if (!userId) return

    const currentDistinctId = jobseekerPosthogClient.get_distinct_id?.()
    if (currentDistinctId && currentDistinctId !== userId) {
        // Explicitly alias the anonymous id to the returned user id so pre/post identify events stitch together.
        jobseekerPosthogClient.alias(userId, currentDistinctId)
    }

    jobseekerPosthogClient.identify(userId, extraProps)
    setJsUserId(userId)
    const email =
        typeof extraProps?.email === 'string' ? extraProps.email.trim() : ''
    if (email) {
        setJsUserEmail(email)
    }
}
